/**
 * Club Gate
 *
 * Single readiness gate for every club-scoped route. While club data is
 * loading (including the React Query retry backoff and the brief window
 * before the query enables), it renders a neutral fallback skeleton. Only a
 * genuine, retries-exhausted failure renders an error. Children render once
 * club data is available.
 *
 * Mounted around the content `<Outlet />` in `MainLayout`, so the header and
 * footer stay rendered and downstream steps can trust that `club` is loaded —
 * eliminating the per-component "Unable to load club information" flash.
 *
 * @module services/internalApi/club/components
 */

import { captureException, captureMessage } from '@butter/monitoring/web';
import { BookingError } from '@butter/ui/domains/booking';
import { PageLoadingSkeleton } from '~/ui/layout/PageLoadingSkeleton';
import { useClubQuery } from '../hooks/useClubQuery';
import {
  useClubQueryState,
  getStateErrorCategory,
  getStateErrorType,
} from '../hooks/useClubQueryState';

interface ClubGateProps {
  children: React.ReactNode;
  /** Placeholder shown while club data loads. Defaults to a neutral skeleton. */
  fallback?: React.ReactNode;
}

export function ClubGate({ children, fallback = <PageLoadingSkeleton /> }: ClubGateProps) {
  const query = useClubQuery();
  const state = useClubQueryState(
    query.data,
    query.isLoading,
    query.isFetching,
    query.clubQueryError,
  );

  // Genuine failure (only after connectivity retries are exhausted, or a
  // validation/config error) — show the error with monitoring context.
  if (state.shouldShowError && state.error) {
    return (
      <BookingError
        title={state.error.title}
        message={state.error.detailedMessage}
        error={state.error.error}
        onCaptureException={captureException}
        onCaptureMessage={captureMessage}
        context={{
          category: getStateErrorCategory(state) ?? 'api_error',
          type: getStateErrorType(state) ?? 'fetch_error',
          severity: 'error',
        }}
      />
    );
  }

  // Anything that is not loaded content yet → skeleton. This covers the
  // initial fetch, the retry backoff, and the momentary window where the
  // query is disabled while the club-id param is being read.
  if (!state.shouldShowContent) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
